import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { FeedPost } from '@/components/feed/FeedPost';
import { VirtualizedFeed } from '@/components/feed/VirtualizedFeed';
import { CreatePollFAB } from '@/components/polls/CreatePollFAB';
import { CreatePollSheet } from '@/components/polls/CreatePollSheet';
import { PollFilterChips, PollFilter } from '@/components/polls/PollFilterChips';
import { PollActivityPulse } from '@/components/polls/PollActivityPulse';
import { LiveActivityToast } from '@/components/feed/LiveActivityToast';
import { PollsEmptyState } from '@/components/empty-states/PollsEmptyState';
import { PullToRefresh } from '@/components/ui/PullToRefresh';
import { FeedSkeleton } from '@/components/skeletons';
import { UniversalSearchSheet } from '@/components/search/UniversalSearchSheet';
import { FeatureLockedState } from '@/components/onboarding/FeatureLockedState';
import { useFeedPolls } from '@/hooks/useFeedPolls';
import { useAuth } from '@/hooks/useAuth';
import { FeedPostData } from '@/types/polls';
import { Vote, Search, Loader2, Video } from 'lucide-react';
import { cn } from '@/lib/utils';


export default function HomePage() {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<PollFilter>('community');
  const [videoOnly, setVideoOnly] = useState(false);
  const { data: dbPolls = [], isLoading } = useFeedPolls({ filter: activeFilter });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSearch, setShowSearch] = useState(false);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const highlightedPollId = searchParams.get('poll');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle navigation state for quick actions from bottom nav
  useEffect(() => {
    const state = location.state as { openCreatePoll?: boolean } | null;
    if (state?.openCreatePoll) {
      // Clear state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      // Open create poll sheet
      setTimeout(() => {
        window.dispatchEvent(new Event('pause-all-feed-videos'));
        setShowCreatePoll(true);
      }, 100);
    }
  }, [location.state, navigate, location.pathname]);

  // Scroll to specific poll when navigating from notification (for non-virtualized feeds)
  // The VirtualizedFeed handles its own scroll-to-poll logic
  useEffect(() => {
    if (highlightedPollId && !isLoading && dbPolls.length > 0 && dbPolls.length < 10) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const pollElement = document.getElementById(`poll-${highlightedPollId}`);
        if (pollElement) {
          pollElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Add highlight animation
          pollElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
          setTimeout(() => {
            pollElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
            // Clear the query param after scrolling
            setSearchParams({}, { replace: true });
          }, 2500);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [highlightedPollId, isLoading, dbPolls, setSearchParams]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['feed-polls', activeFilter] }),
      queryClient.invalidateQueries({ queryKey: ['social-proof-stats'] }),
    ]);
  }, [queryClient, activeFilter]);

  // Use only real database polls - no mock data
  const allPosts: FeedPostData[] = dbPolls;
  const filteredPosts = videoOnly ? allPosts.filter(post => post.videoUrl) : allPosts;

  // Determine if we have enough posts to benefit from virtualization
  const shouldVirtualize = filteredPosts.length >= 10;

  const searchButton = (
    <button
      onClick={() => setShowSearch(true)}
      className="glass-bubble w-10 h-10"
    >
      <Search className="w-[18px] h-[18px] header-glass-icon" strokeWidth={2} />
    </button>
  );

  // Show loading state while auth is rehydrating
  if (authLoading) {
    return (
      <div className="h-full flex flex-col bg-background">
        <Header showAvatar showNotification />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Not logged in - show nice empty state with background
  if (!user) {
    return (
      <div className="h-full flex flex-col bg-background">
        <Header showAvatar showNotification />
        <FeatureLockedState
          featureName="Vote"
          icon={Vote}
          title="Get opinions from the community"
          subtitle="Ask for style advice and vote on looks"
          backgroundType="polls"
          signedOut
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <Header showAvatar showNotification rightContent={searchButton} />

      {/* PullToRefresh as the single scroll container */}
      <div className="flex-1 overflow-hidden">
        <PullToRefresh onRefresh={handleRefresh} className="h-full" scrollRef={scrollContainerRef} scrollClassName="header-inset pb-nav">
          {/* Sticky tab nav inside scroll container */}
          <div className="sticky top-0 z-30 tab-nav-glass">
            <div className="flex items-center justify-between px-5">
              <div className="flex gap-6">
                {[
                  { id: 'community' as PollFilter, label: 'Community' },
                  { id: 'following' as PollFilter, label: 'Friends' },
                ].map((tab) => {
                  const isActive = activeFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveFilter(tab.id)}
                      className={cn(
                        "py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                        isActive ? "tab-active" : ""
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setVideoOnly(v => !v)}
                className={cn(
                  "p-2 rounded-full transition-colors",
                  videoOnly ? "text-primary bg-primary/10" : "text-muted-foreground"
                )}
                aria-label="Toggle video-only polls"
              >
                <Video className="w-4 h-4" />
              </button>
            </div>
          </div>

          {isLoading ? (
            <FeedSkeleton count={3} />
          ) : filteredPosts.length === 0 ? (
            <PollsEmptyState onCreatePoll={() => setShowCreatePoll(true)} />
          ) : shouldVirtualize ? (
            <VirtualizedFeed posts={filteredPosts} className="h-full" />
          ) : (
            <div className="divide-y divide-white/5">
              {filteredPosts.map((post, index) => (
                <div 
                  key={post.id} 
                  id={`poll-${post.id}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  className="transition-all duration-300"
                >
                  <FeedPost post={post} isFirstInFeed={index === 0} />
                </div>
              ))}
            </div>
          )}
        </PullToRefresh>
      </div>

      <CreatePollFAB scrollRef={scrollContainerRef} />
      <CreatePollSheet open={showCreatePoll} onOpenChange={setShowCreatePoll} />
      
      <UniversalSearchSheet 
        open={showSearch} 
        onOpenChange={setShowSearch} 
      />
    </div>
  );
}
